import { Search, Plus, ArrowRight } from "lucide-react";
import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export default function PatientPicker({ onSelect, onNewPlan, patients }) {
  const [pid, setPid] = useState("");

  const handleLookup = (e) => {
    e.preventDefault();
    const match = patients.find(
      (p) => p.id.toLowerCase() === pid.trim().toLowerCase()
    );
    if (match) onSelect(match.id);
  };

  return (
    <div
      data-testid="patient-picker"
      className="bg-white rounded-3xl border border-slate-200/80 shadow-soft p-8 md:p-12"
    >
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D1FAE5] text-[#065F46] font-figtree text-[11px] uppercase tracking-[0.18em] font-semibold mb-5">
          Get started
        </div>
        <h1 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
          Pick a patient or start a new plan.
        </h1>
        <p className="mt-3 font-figtree text-base text-[#475569] leading-relaxed">
          Type a Patient ID from your main system to jump straight to that
          patient's history, or start a fresh plan in seconds. We only ever store
          the ID and initials — no PHI.
        </p>

        <form onSubmit={handleLookup} className="mt-7 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              data-testid="patient-picker-input"
              value={pid}
              onChange={(e) => setPid(e.target.value)}
              placeholder="e.g., PT-1042"
              className="pl-10 h-12 rounded-xl bg-[#F8FAFC] border-slate-200 focus-visible:ring-[#10B981] font-figtree"
            />
          </div>
          <Button
            data-testid="patient-picker-find-btn"
            type="submit"
            variant="outline"
            className="h-12 rounded-xl border-slate-200 hover:bg-slate-50 font-figtree text-sm font-medium px-6"
          >
            Find patient
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>

        <div className="mt-4">
          <Button
            data-testid="patient-picker-new-plan-btn"
            onClick={onNewPlan}
            className="rounded-xl bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-sm font-medium h-12 px-6"
          >
            <Plus className="w-4 h-4 mr-2" />
            New plan
          </Button>
        </div>
      </div>

      <div className="mt-12">
        <div className="font-figtree text-xs uppercase tracking-[0.18em] font-semibold text-[#94A3B8] mb-3">
          Recently seen — sample patients
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {patients.slice(0, 4).map((p) => (
            <button
              key={p.id}
              data-testid={`patient-picker-recent-${p.id}`}
              onClick={() => onSelect(p.id)}
              className="flex items-center gap-3 p-3 rounded-2xl border border-slate-200 hover:border-[#10B981]/50 hover:bg-[#F0FDF4] transition-colors text-left"
            >
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#D1FAE5] text-[#065F46] font-outfit text-sm font-bold">
                {p.initials}
              </span>
              <div className="min-w-0">
                <div className="font-outfit text-sm font-semibold text-[#0F172A]">
                  {p.id}
                </div>
                <div className="font-figtree text-xs text-[#64748B]">
                  {(p.plans?.length ?? p.plan_count ?? 0)}{" "}
                  {(p.plans?.length ?? p.plan_count ?? 0) === 1 ? "plan" : "plans"} on file
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
